import { useLocation } from "react-router";
import type { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  RetryStripePaymentButton,
  RetrySellarCreditPaymentButton,
} from "../../stockList/checkoutV2/CheckoutV2SuccessPageWithFeed";
import { PaidOrderCard } from "./PaidOrderCard";
import { Alert } from "~/components/alert/Alert";
import {
  UnifiedCheckoutOrderPaymentState,
  useUnifiedCheckoutOrderPaymentStatus,
} from "../hooks/useUnifiedCheckoutOrderPaymentStatus";

export const UnifiedCheckoutOrderPaymentStatus = ({
  order,
  showOrderCard = false,
}: {
  order: OrderGetResponseType;
  showOrderCard?: boolean;
}) => {
  const paymentState = useUnifiedCheckoutOrderPaymentStatus(order);

  return (
    <UnifiedCheckoutOrderPaymentStatusContent
      order={order}
      showOrderCard={showOrderCard}
      paymentState={paymentState}
    />
  );
};

export const UnifiedCheckoutOrderPaymentStatusContent = ({
  order,
  paymentState,
  showOrderCard = false,
}: {
  order: OrderGetResponseType;
  paymentState: UnifiedCheckoutOrderPaymentState;
  showOrderCard?: boolean;
}) => {
  const location = useLocation();
  const { isAsyncPayment, isError, isFailed, isPending, failureReason } =
    paymentState;

  const returnPath = location.pathname.endsWith("/pay")
    ? `${location.pathname}?paymentReturn=stripe&supplierId=${order.SupplierId}&orderId=${order.id}`
    : location.pathname;

  return (
    <div className="flex flex-col gap-2">
      {showOrderCard && (
        <PaidOrderCard
          supplierName={order.Supplier.Business.name}
          itemCount={order.OrderItems.reduce(
            (count, item) => count + item.quantity,
            0,
          )}
          amount={order.price}
          currency={order.currency as "gbp" | "sgd"}
          paymentStatus={
            isFailed ? "failed" : isPending ? "pending" : "succeeded"
          }
          paymentFailureReason={failureReason}
          paymentRetry={
            <PaymentRetryAction
              order={order}
              returnPath={returnPath}
              failed={isFailed}
            />
          }
        />
      )}
      {isPending && (
        <>
          {!showOrderCard && isFailed && (
            <div className="p-4 pt-0">
              <Alert type="error" message="" className="w-full">
                <div className="text-base text-zinc-800">
                  <p className="font-semibold">Payment failed</p>
                  <p>Reason: {failureReason}</p>
                </div>

                <PaymentRetryAction
                  order={order}
                  returnPath={returnPath}
                  failed
                />
              </Alert>
            </div>
          )}
          {!isFailed && (
            <p role="status" className="text-sm text-amber-800">
              {isError
                ? "We couldn't check your payment status. Please check your order for updates."
                : "Confirming your payment…"}
            </p>
          )}
        </>
      )}
      {!showOrderCard && !isPending && isAsyncPayment && (
        <div
          role="status"
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800"
        >
          Payment sent
        </div>
      )}
    </div>
  );
};

const PaymentRetryAction = ({
  order,
  returnPath,
  failed,
}: {
  order: OrderGetResponseType;
  returnPath: string;
  failed: boolean;
}) => {
  if (order.paymentType === "sellarCredit") {
    return <RetrySellarCreditPaymentButton orderId={order.id} />;
  }

  if (order.paymentType !== "checkout" || !order.Payment?.paymentIntentId) {
    return null;
  }

  return (
    <RetryStripePaymentButton
      stripeAccountId={order.Supplier.stripeAccountId ?? ""}
      supplierName={order.Supplier.Business.name}
      paymentAmount={order.price}
      paymentIntentId={order.Payment.paymentIntentId}
      returnPath={returnPath}
      asLink={!failed}
      linkClassName={failed ? undefined : "self-end text-xs cursor-pointer"}
      buttonText={failed ? "Retry payment" : undefined}
    />
  );
};
